// @ts-nocheck
import _ from 'lodash';
import qs from 'query-string';
import { toast } from 'react-toastify';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { produce } from 'immer';
import { DataGridProProps, GRID_TREE_DATA_GROUPING_FIELD, GridEvents, useGridApiRef } from '@mui/x-data-grid-pro';
import { CustomDataTable } from 'components/DatatableComponent';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import LinearProgress from '@mui/material/LinearProgress';
import CubeDimensionalViewModal, { CubeDimensionalViewPageProps } from '../CubeDimensionalViewPage';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { RootState } from 'store';
import {
  CubeDataEntryPanelOptionValues,
  CubeDataEntrySettingPanel,
  CubeDataEntryShowDataDetails,
} from './components/CubeDataEntryPanel';
import {
  useGetCubeActivityStatus,
  useGetCubeCurrentTimePeriodDate,
  useGetCubeDataEntry,
  useGetCubeDataEntryBaseValue,
  useGetCubeDataEntryInitialValue,
  useSyncCubeData,
  useUpsertCubeDataEntryBase,
  useUpsertCubeDataEntryInitial,
  useUpsertCubeDataentry,
} from 'services/v1/Tenant/AROCube/CubeDataEntryService';
import {
  CubeDataEntryAttribute,
  RequestUpsertCubeDataEntry,
  SpecialDataEntryInputType,
} from 'types/api/Tenant/AROCube/CubeDataEntryTypes';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { getErrorMessage, getErrorTitle } from 'utils/Error';
import { CUBE_ACTIVITY_STATUS, CUBE_LAYER_TYPE, CUBE_PERCENTAGE_TYPE, CUBE_SYNC_STATUS } from 'constant/CubeConstant';
import { RULES_CONSTANT } from 'constant/RuleConstant';
import { ModalLeavePageConfirmationComponent } from 'components/ModalComponent/ModalLeavePageConfirmationComponent';
import { formatCubeNumber } from 'utils/NumberUtils';
import {
  getCellClassName,
  jsonToGridData,
  jsonToGridDataSpecialEntry,
  transformData,
  transformDataSpecialEntry,
} from './utils/CubeDataEntryUtils';
import { CustomGridTreeDataGroupingCell } from './components/CustomGridTreeDataGroupingCell';
import {
  generateBreadCumbItemsV2,
  TenantHomeDashboardNavigationBar,
} from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import { useGetCubeDefinitionDetails } from 'services/v1/SystemTenant/AROCube/CubeBuilderService';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ModalInfoComponent, { ModalInfoProps } from 'components/ModalComponent/ModalInfoComponent';
import { dayjs } from 'utils/DateUtils';
import { ENV_CONFIG } from 'config/env';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { ROLES } from 'constant/PermissonConstant';
import { checkAccess } from 'routes/PrivateRoute';
import { useGetBreadcumbItems } from 'services/v1/Common/NavBarMenuService';
import { NAVIGATION_COMPONENT_TYPE } from 'constant/NavigationConstant';
import { PATH_CONSTANT } from 'constant/PathConstant';
import { AnimatedPage } from 'components/AnimatedPage';
import { ModalCubeEventDataDetails } from './components/ModalEventData';
import { getPageAnimationByRefNavigation } from 'utils/Animation';
import { CubeEntryRightClickMenu, CubeEntryRightClickMenuAction } from './components/CubeEntryRightClickMenu';
import { HoverPopperProvider } from './components/HopperPopperAttributeProvider';
import { ModalRepeatValues } from './components/ModalRepeatValues';
import { ModalCellInput } from './components/ModalCellInput';
import { CubeEntryLegend } from './components/CubeEntryLegend';
import { ModalConfirmationComponent } from 'components/ModalComponent/ModalConfirmationComponent';
import { ModalCubeChartDisplay } from './components/ModalCubeChartDisplay';
import { FormCubeChartDisplayParameter } from './components/CubeChartDisplayButton';
import { ModalDeveloperToolsAudit } from './components/ModalDeveloperToolsAudit';
import { ModalDeveloperToolsReferenceAudit } from './components/ModalDeveloperToolsReferenceAudit';

// Coerce null / undefined / empty string row values to 0 on load
function sanitizeRowValues(rows: any[]) {
  return rows.map((row) => {
    const r = { ...row };
    Object.keys(r).forEach((k) => {
      if (k !== 'rowid' && k !== 'id' && k !== 'path' && k !== 'name') {
        const v = r[k];
        if (v === null || v === undefined || v === '' || String(v).trim() === '') r[k] = 0;
      }
    });
    return r;
  });
}

// Performance optimization: Global caches
const rangeCache = new Map();
const domCache = new Map();

// Performance optimization: Cache keys for consistency
const CACHE_KEYS = {
  RANGE: (a: any, b: any) => `${a.id}-${a.field}-${b.id}-${b.field}`,
  DOM: (selector: string) => `dom-${selector}`,
};

// Performance optimization: Stable normalize range function
function normalizeRange(
  a: { id: any; field: string },
  b: { id: any; field: string },
  rowIndexById: Map<any, number>,
  colIndexByField: Map<string, number>,
) {
  // Performance optimization: Check cache first
  const cacheKey = CACHE_KEYS.RANGE(a, b);
  const cached = rangeCache.get(cacheKey);
  if (cached) return cached;

  const r1 = rowIndexById.get(a.id),
    r2 = rowIndexById.get(b.id);
  const c1 = colIndexByField.get(a.field),
    c2 = colIndexByField.get(b.field);
  if (r1 == null || r2 == null || c1 == null || c2 == null) return null;

  // Performance optimization: Use direct comparisons instead of Math.min/max
  const rMin = r1 < r2 ? r1 : r2;
  const rMax = r1 > r2 ? r1 : r2;
  const cMin = c1 < c2 ? c1 : c2;
  const cMax = c1 > c2 ? c1 : c2;

  const result = { rMin, rMax, cMin, cMax };

  // Performance optimization: Cache the result
  rangeCache.set(cacheKey, result);

  return result;
}

const TabStyle = {
  textTransform: 'none',
  '&.Mui-selected': {
    color: '#42BB93',
  },
};
const TabIndicatoryStyle = {
  style: {
    backgroundColor: '#42BB93',
  },
};

const moreDetailsTextActionStyle = {
  color: '#42BB93',
  cursor: 'pointer',
  textDecoration: 'underline',
};

const CUBE_DATA_TABS = {
  CUBE_ENTRY: 'CUBE_ENTRY',
  INITIAL_VALUE: 'INITIAL_VALUE',
  BASE_VALUE: 'BASE_VALUE',
};

const initialStateModalDetails = {
  timePeriod: null,
  cubeMember: null,
  eventSubjectId: null,
  eventSubjectName: null,
  open: false,
};

async function copyToClipboard(text: string) {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
  } else {
    // fallback for older browsers
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
}

export default function CubePage() {
  const cubeSettingPanelRef = useRef<{
    isLoadingOptionsData: boolean;
  }>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const navigationRef = searchParams.get('ref');
  const locations = useLocation();
  const apiRef = useGridApiRef();
  const existingSearchParams = qs.parse(locations.search);
  const { cubeId } = useParams<{ cubeId: string }>();
  const { auth: authReducer } = useSelector((state: RootState) => state);
  // Activity status is polled from its own lightweight endpoint so it stays fresh
  // while the cube definition itself remains cached.
  const { data: cubeActivityStatusData } = useGetCubeActivityStatus(cubeId, {
    refetchInterval: 5000,
  });
  const cubeActivityStatus = cubeActivityStatusData?.data?.activityStatus ?? null;
  const { data: cubeDefinitionDetails, error: errorCubeDefinitionDetails } = useGetCubeDefinitionDetails(cubeId);

  const { data: cubeCurrentDate } = useGetCubeCurrentTimePeriodDate(cubeId);
  const [modalInfo, setModalInfo] = useState<ModalInfoProps>({
    title: '',
    descriptionMessage: '',
    visible: false,
  });
  const [repeatValuesModal, setRepeatValuesModal] = useState<{ open: boolean; id: any; field: string }>({
    open: false,
    id: null,
    field: '',
  });
  const [cellInputModal, setCellInputModal] = useState<{ open: boolean; id: any; field: string }>({
    open: false,
    id: null,
    field: '',
  });
  const [tabChangeModal, setTabChangeModal] = useState<{ open: boolean; pendingTab: string | null }>({
    open: false,
    pendingTab: null,
  });
  const [rowData, setRowData] = useState<any>([]);
  const [initialData, setInitialData] = useState<any>([]);
  const [optionValue, setOptionValue] = useState<{
    subjectId: string | undefined;
    cubeId: string | undefined;
    layerId: string | undefined;
    memberId: string | undefined;
    xMemberId: string | undefined;
    timeIntervalId: string | undefined;
  }>({
    subjectId: undefined,
    cubeId: cubeId,
    layerId: undefined,
    memberId: undefined,
    xMemberId: undefined,
    timeIntervalId: undefined,
  });

  const [modalDataDetails, setModalDataDetails] = useState<{
    open: boolean;
    eventSubjectId: string | null;
    eventSubjectName: string | null;
    cubeMember: string | null;
    timePeriod: string | null;
    timeInterval: string | null;
  }>(initialStateModalDetails);
  const [cubeDimensionalViewModalState, setCubeDimensionalViewModalState] = useState<CubeDimensionalViewPageProps>({
    cubeId: undefined,
    open: false,
  });

  const [selectedTab, setSelectedTab] = useState(CUBE_DATA_TABS.CUBE_ENTRY);
  const [isLoadingOptionsData, setIsLoadingOptionsData] = useState(false);
  const [chartDisplayModal, setChartDisplayModal] = useState<{
    open: boolean;
    layerIds?: string[];
    initialAttributes?: AutoCompleteItem[];
    chartType?: string;
  }>({ open: false });
  const [developerToolsModal, setDeveloperToolsModal] = useState<{ open: boolean }>({ open: false });
  const [referenceAuditModal, setReferenceAuditModal] = useState<{ open: boolean }>({ open: false });
  const lastScrollRef = useRef<{ left: number; top: number }>({ left: 0, top: 0 });

  // === Range selection state ===
  const rangeContainerRef = useRef<HTMLDivElement | null>(null);
  const [anchor, setAnchor] = useState<{ id: GridRowId; field: string } | null>(null);
  const [focus, setFocus] = useState<{ id: GridRowId; field: string } | null>(null);
  const draggingRef = useRef(false);

  const mousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);

  const EDGE_PX = 24; // how close to an edge before we scroll
  const MAX_SPEED = 20; // px per frame
  function speedFor(distance: number) {
    // distance is how far inside the edge zone (0..EDGE_PX)
    return Math.min(MAX_SPEED, Math.round((distance / EDGE_PX) * MAX_SPEED));
  }
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const isSingleCell = !anchor || !focus || (anchor.id === focus.id && anchor.field === focus.field);
  // const hasRange = !!anchor && !!focus;

  const copiedRef = useRef<{ value: any; isRange?: boolean } | null>(null);
  const hasClipboard = !!copiedRef.current;

  const MAX_UNDO = 50;
  const undoStackRef = useRef<any[][]>([]);
  const redoStackRef = useRef<any[][]>([]);

  const isAnyUnsaved = useMemo(() => {
    return !_.isEqual(rowData, initialData);
  }, [rowData, initialData]);

  const isDataEditable = useMemo(() => {
    return checkAccess(
      [ROLES.BUILDER, ROLES.ADMIN, ROLES.TESTER, ROLES.SUPPORT, ROLES.PACKAGER],
      authReducer.threadUserAsRole,
    );
  }, [authReducer.threadUserAsRole]);

  const {
    data: cubeEntryData,
    isLoading: isLoadingDataEntry,
    isError: isErrorCubeData,
    error: errorCubeData,
    refetch: refetchCubeData,
  } = useGetCubeDataEntry(
    optionValue.cubeId,
    optionValue.layerId,
    optionValue.memberId,
    optionValue.xMemberId,
    optionValue.timeIntervalId,
  );

  const {
    data: cubeEntryDataBaseValue,
    isLoading: isLoadingDataBaseValue,
    error: errorCubeDataBaseValue,
  } = useGetCubeDataEntryBaseValue(cubeId, optionValue.memberId, optionValue.xMemberId);

  const {
    data: cubeEntryDataInitialValue,
    isLoading: isLoadingDataInitialValue,
    error: errorCubeDataInitialValue,
  } = useGetCubeDataEntryInitialValue(cubeId, optionValue.memberId, optionValue.xMemberId);

  const { mutate: saveCubeDataEntry, isLoading: isSubmitting } = useUpsertCubeDataentry();
  const { mutate: saveCubeDataEntryBaseValue, isLoading: isSubmittingBaseValue } = useUpsertCubeDataEntryBase();
  const { mutate: saveCubeDataEntryInitialValue, isLoading: isSubmittingInitialValue } =
    useUpsertCubeDataEntryInitial();

  const { data: breadCrumbItems } = useGetBreadcumbItems(cubeId, NAVIGATION_COMPONENT_TYPE.CUBE);

  const { mutate: syncCubeData } = useSyncCubeData();

  const error = errorCubeData || errorCubeDataBaseValue || errorCubeDataInitialValue || errorCubeDefinitionDetails;

  const cubeSpecialDataEntry: AxiosResponse<ResponseCubeSpecialDataEntry> | null = useMemo(() => {
    if (selectedTab === CUBE_DATA_TABS.BASE_VALUE) {
      return cubeEntryDataBaseValue;
    }
    if (selectedTab === CUBE_DATA_TABS.INITIAL_VALUE) {
      return cubeEntryDataInitialValue;
    }
    return null;
  }, [cubeEntryDataBaseValue, cubeEntryDataInitialValue, selectedTab]);

  const percentageRenderAttributes = useMemo(() => {
    if (cubeEntryData?.data) {
      return cubeEntryData?.data.attributes
        .filter((attr) => attr.percentage === CUBE_PERCENTAGE_TYPE.PERCENTAGE)
        .map((attr) => attr.name);
    }
    return [];
  }, [cubeEntryData?.data]);

  const decimalPlacesRenderAttributes: { [key: string]: number } = useMemo(() => {
    if (cubeEntryData?.data) {
      return cubeEntryData?.data.attributes.reduce((acc, attr) => {
        acc[attr.name] = attr.decimal;
        return acc;
      }, {} as { [key: string]: number });
    }
    return {};
  }, [cubeEntryData?.data]);

  const percentageRenderAttributesSpecialEntry: { [key: string]: number } = useMemo(() => {
    if (cubeSpecialDataEntry?.data) {
      return cubeSpecialDataEntry?.data.attributes
        .filter((attr) => attr.percentage === CUBE_PERCENTAGE_TYPE.PERCENTAGE)
        .map((attr) => attr.name);
    }
    return {};
  }, [cubeSpecialDataEntry?.data]);

  const decimalPlacesRenderAttributesSpecialEntry: { [key: string]: number } = useMemo(() => {
    if (cubeSpecialDataEntry?.data) {
      return cubeSpecialDataEntry?.data.attributes.reduce((acc, attr) => {
        acc[attr.name] = attr.decimal;
        return acc;
      }, {} as { [key: string]: number });
    }
    return {};
  }, [cubeSpecialDataEntry?.data]);

  const inputInitialAttributes: string[] = useMemo(() => {
    if (cubeEntryData?.data) {
      return cubeEntryData.data.attributes
        .filter((attrItem) => attrItem.ruleTypeCode === RULES_CONSTANT.CUBE.RULE_CUBE_INPUT_INITIAL)
        .map((attribute) => attribute.name);
    }
    return [];
  }, [cubeEntryData?.data]);

  const readOnlyAttributes: string[] = useMemo(() => {
    switch (selectedTab) {
      case CUBE_DATA_TABS.CUBE_ENTRY:
        return cubeEntryData?.data.attributes.filter((attribute) => !attribute.isEditable).map((attr) => attr.name);
      case CUBE_DATA_TABS.BASE_VALUE:
        return cubeEntryDataBaseValue?.data.attributes
          .filter((attribute) => !attribute.isEditable)
          .map((attr) => attr.name);
      case CUBE_DATA_TABS.INITIAL_VALUE:
        return cubeEntryDataInitialValue?.data.attributes
          .filter((attribute) => !attribute.isEditable)
          .map((attr) => attr.name);

      default:
        return [];
    }
  }, [cubeEntryData?.data, cubeEntryDataBaseValue?.data, cubeEntryDataInitialValue?.data, selectedTab]);

  const calculatedAttributes = useMemo(() => {
    switch (selectedTab) {
      case CUBE_DATA_TABS.CUBE_ENTRY:
        return cubeEntryData?.data.attributes.filter((attribute) => attribute.isCalculated).map((attr) => attr.name);
      case CUBE_DATA_TABS.BASE_VALUE:
        return cubeEntryDataBaseValue?.data.attributes
          .filter((attribute) => attribute.isCalculated)
          .map((attr) => attr.name);
      case CUBE_DATA_TABS.INITIAL_VALUE:
        return cubeEntryDataInitialValue?.data.attributes
          .filter((attribute) => attribute.isCalculated)
          .map((attr) => attr.name);
      default:
        return [];
    }
  }, [cubeEntryData?.data, cubeEntryDataBaseValue?.data, cubeEntryDataInitialValue?.data, selectedTab]);

  const pullFromEventAttributesMap = useMemo(() => {
    return new Map(
      cubeEntryData?.data.attributes
        .filter((attribute) => attribute.ruleTypeCode === RULES_CONSTANT.CUBE.RULE_CUBE_PULL_FROM_EVENT)
        .map((attr) => [attr.name, attr]),
    );
  }, [cubeEntryData?.data.attributes]);

  console.log({
    pullFromEventAttributesMap,
  });

  const tabsMenu = useMemo(() => {
    if (cubeEntryData?.data) {
      if (cubeEntryData?.data.layer.layerType !== CUBE_LAYER_TYPE.BASELINE || optionValue.timeIntervalId !== 'BASE') {
        return [
          {
            value: CUBE_DATA_TABS.CUBE_ENTRY,
            label: 'Data Entry',
          },
        ];
      }

      const allBaseLineTabs = [
        {
          value: CUBE_DATA_TABS.CUBE_ENTRY,
          label: 'Data Entry',
        },
      ];

      if (cubeEntryData?.data?.baseAttributes.length) {
        allBaseLineTabs.push({
          value: CUBE_DATA_TABS.BASE_VALUE,
          label: `Base Value (${cubeEntryData?.data?.baseAttributes.length})`,
        });
      }

      if (cubeEntryData?.data?.initialAttributes.length) {
        allBaseLineTabs.push({
          value: CUBE_DATA_TABS.INITIAL_VALUE,
          label: `Initial Value (${cubeEntryData?.data?.initialAttributes.length})`,
        });
      }
      return allBaseLineTabs;
    }
    return [];
  }, [cubeEntryData?.data, optionValue.timeIntervalId]);

  const columns = useMemo(() => {
    if (cubeEntryData?.data && selectedTab === CUBE_DATA_TABS.CUBE_ENTRY) {
      return [
        ...Object.keys(cubeEntryData?.data.data).map((timePeriod) => ({
          field: timePeriod,
          headerName: timePeriod,
          type: 'number',
          editable: true,
          minWidth: 120,
          headerClassName: 'custom-cube-tree-data-header',
          cellClassName: (params) => getCellClassName(params, initialData),
          renderCell: (params) => {
            const attributeName = params.id.split('#')[0];
            const isValueClickAble = pullFromEventAttributesMap.has(attributeName);
            const decimalPlaces = decimalPlacesRenderAttributes?.[attributeName] || 0;

            let value;
            if (percentageRenderAttributes?.includes(attributeName)) {
              value = formatCubeNumber(params?.value ?? 0, decimalPlaces, true);
            } else {
              value = formatCubeNumber(params?.value ?? 0, decimalPlaces);
            }

            if (isValueClickAble) {
              return (
                <span
                  style={{ color: 'blue', textDecoration: 'none', cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleOnCellValueClick({
                      attributeName,
                      timePeriod,
                    });
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.textDecoration = 'none';
                  }}
                >
                  {value}
                </span>
              );
            }

            return value;
          },
        })),
      ];
    }
    if (!cubeEntryData?.data && CUBE_DATA_TABS.CUBE_ENTRY === selectedTab) return [];

    // For Special Entry Group of  Members
    if (selectedTab !== CUBE_DATA_TABS.CUBE_ENTRY && cubeSpecialDataEntry?.data) {
      return [
        ...Object.keys(cubeSpecialDataEntry?.data.data).map((cubeMember) => ({
          field: cubeMember,
          headerName:
            cubeSpecialDataEntry?.data.inputType === SpecialDataEntryInputType.MULTI_MEMBER_INPUT
              ? cubeMember
              : 'Value',
          type: 'number',
          editable: true,
          minWidth: 120,
          headerClassName: 'custom-cube-tree-data-header',
          cellClassName: (params) => getCellClassName(params, initialData),
          renderCell: (params) => {
            const attributeName = params.id.split('#')[0];
            const decimalPlaces = decimalPlacesRenderAttributesSpecialEntry?.[attributeName] || 0;
            let value;
            if (percentageRenderAttributesSpecialEntry?.includes(attributeName)) {
              value = formatCubeNumber(params?.value ?? 0, decimalPlaces, true);
            } else {
              value = formatCubeNumber(params?.value ?? 0, decimalPlaces);
            }
            return value;
          },
        })),
      ].sort((a, b) => a.headerName.localeCompare(b.headerName));
    }

    // For Special Entry Single Member
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cubeEntryData?.data,
    selectedTab,
    initialData,
    percentageRenderAttributes,
    decimalPlacesRenderAttributes,
    percentageRenderAttributesSpecialEntry,
    decimalPlacesRenderAttributesSpecialEntry,
    pullFromEventAttributesMap,
  ]);

  const firsTimePeriod = useMemo(() => {
    if (cubeEntryData?.data) {
      const timePeriod = cubeEntryData?.data?.data;
      return Object.keys(timePeriod)?.[0] || null;
    }
    return null;
  }, [cubeEntryData?.data]);
  useEffect(() => {
    if (selectedTab === CUBE_DATA_TABS.CUBE_ENTRY && cubeEntryData?.data) {
      const entryData = sanitizeRowValues(jsonToGridData(cubeEntryData?.data, false));
      setRowData(entryData);
      setInitialData(entryData);
      undoStackRef.current = [];
      redoStackRef.current = [];
    }
    if (selectedTab === CUBE_DATA_TABS.BASE_VALUE && cubeEntryDataBaseValue?.data) {
      const data = sanitizeRowValues(jsonToGridDataSpecialEntry(cubeEntryDataBaseValue?.data));
      setRowData(data);
      setInitialData(data);
      undoStackRef.current = [];
      redoStackRef.current = [];
    }
    if (selectedTab === CUBE_DATA_TABS.INITIAL_VALUE && cubeEntryDataInitialValue?.data) {
      const initialData = sanitizeRowValues(jsonToGridDataSpecialEntry(cubeEntryDataInitialValue?.data));
      setRowData(initialData);
      setInitialData(initialData);
      undoStackRef.current = [];
      redoStackRef.current = [];
    }
  }, [cubeEntryData?.data, selectedTab, cubeEntryDataBaseValue?.data, cubeEntryDataInitialValue?.data]);

  // 1. Subscribe to grid scroll events and track last non-zero position
  useEffect(() => {
    const api = apiRef.current;
    if (!api?.subscribeEvent || !api.getScrollPosition) return;

    const handleRowScroll = (_: GridScrollParams) => {
      const pos = api.getScrollPosition();
      if (!pos) return;

      const { left, top } = pos;
      // Ignore if still at initial (0, 0) or unchanged
      if ((left === 0 && top === 0) || (left === lastScrollRef.current.left && top === lastScrollRef.current.top)) {
        return;
      }
      lastScrollRef.current = { left, top };
    };

    const unsubscribe = api.subscribeEvent(GridEvents.rowsScroll, handleRowScroll);
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef?.current]);

  // 2. On initial load of cubeCurrentDate, jump two columns past "today" if no scroll has occurred
  useEffect(() => {
    const api = apiRef.current;
    if (!api || !cubeCurrentDate?.data) return;

    const todayIndex = cubeCurrentDate.data.currentTimePeriodIndex ?? 0;
    // Only jump if we've never scrolled yet
    if (lastScrollRef.current.left === 0 && lastScrollRef.current.top === 0) {
      const id = setTimeout(() => {
        api.scrollToIndexes({ colIndex: todayIndex + 2 });
      }, 150);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cubeCurrentDate?.data, apiRef.current]);

  // 3. Whenever cubeEntryData changes, restore the last known scroll position
  useEffect(() => {
    const api = apiRef.current;
    if (!api || !cubeEntryData?.data) return;

    const id = setTimeout(() => {
      api.scroll({
        left: lastScrollRef.current.left,
        top: lastScrollRef.current.top,
      });
    }, 100);
    return () => clearTimeout(id);
  }, [cubeEntryData, apiRef]);

  useEffect(() => {
    lastScrollRef.current = { left: 0, top: 0 }; // Reset scroll position on cubeId change
  }, [cubeId]);

  useEffect(() => {
    const handleUnload = (e) => {
      if (isAnyUnsaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [isAnyUnsaved]);

  const breadCrumbs = useMemo(() => {
    return generateBreadCumbItemsV2(breadCrumbItems?.data);
  }, [breadCrumbItems?.data]);

  const groupingColDef: DataGridProProps['groupingColDef'] = useMemo(() => {
    if (!cubeEntryData?.data) return;

    const cellDescription = cubeEntryData?.data.attributes.reduce((acc, attr) => {
      acc[attr.displayName] = attr.description;
      return acc;
    }, {} as Record<string, string>);
    return {
      headerName: '',
      pinnable: true,
      filterable: true,
      leafField: 'name',
      minWidth: 200,
      maxWidth: 400,
      cellClassName: 'custom-cube-tree-data-grouping-cell',
      headerClassName: 'custom-cube-tree-data-header',
      renderCell: (props) => {
        return <CustomGridTreeDataGroupingCell {...props} cellDescription={cellDescription} />;
      },
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cubeEntryData?.data.attributes]);

  const handleOnOptionChange = (data: CubeDataEntryPanelOptionValues) => {
    setOptionValue({
      subjectId: data.subject?.value,
      cubeId: data.cube?.value,
      layerId: data.layer?.value,
      memberId: data.member?.value,
      xMemberId: data.xMember?.value,
      timeIntervalId: data.timeInterval?.value,
    });
  };

  const handleOnTabChange = (_event: any, newValue: string) => {
    if (isAnyUnsaved) {
      setTabChangeModal({ open: true, pendingTab: newValue });
    } else {
      setSelectedTab(newValue);
    }
  };

  const handleOnShowDataDetails = useCallback(
    (data: CubeDataEntryShowDataDetails) => {
      setModalDataDetails({
        eventSubjectName: data.eventSubjectName,
        eventSubjectId: data.eventSubjectId,
        cubeMember: data.cubeMember || optionValue.memberId,
        timePeriod: data.timePeriod,
        timeInterval: data.timeInterval,
        open: true,
      });
    },
    [optionValue.memberId],
  );

  const handleOnDisplayChart = useCallback((data: FormCubeChartDisplayParameter) => {
    setChartDisplayModal({
      open: true,
      layerIds: data.layer.map((l) => l.value?.toString()),
      initialAttributes: data.attribute,
      chartType: data.chartType,
    });
  }, []);

  const handleOnCellValueClick = useCallback(
    ({ attributeName, timePeriod }: { attributeName: string; timePeriod: string }) => {
      const cubeAttribute: CubeDataEntryAttribute | null = pullFromEventAttributesMap.get(attributeName);
      const attributeSubject = cubeDefinitionDetails?.data.attributes.find((attr) => attr.id === cubeAttribute?.id);
      if (attributeSubject && timePeriod) {
        handleOnShowDataDetails({
          timePeriod,
          cubeMember: cubeEntryData?.data.member?.value,
          eventSubjectId: attributeSubject.subjectEvent.value,
          eventSubjectName: attributeSubject.subjectEvent.label,
          timeInterval: optionValue.timeIntervalId,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      optionValue.memberId,
      cubeDefinitionDetails?.data.attributes,
      pullFromEventAttributesMap,
      optionValue.timeInterval,
    ],
  );

  const handleOnSaveData = () => {
    const payload: RequestUpsertCubeDataEntry = {
      cubeId: cubeId ?? '',
      layerId: optionValue.layerId ?? '',
      memberId: optionValue.memberId ?? '',
      xmemberId: optionValue.xMemberId ?? null,
      data:
        selectedTab === CUBE_DATA_TABS.CUBE_ENTRY
          ? transformData(rowData, cubeEntryData?.data?.data)
          : transformDataSpecialEntry(rowData, initialData, cubeSpecialDataEntry?.data),
    };

    const toastId = toast.loading('Saving data...');

    const onSuccess = () => {
      toast.update(toastId, { render: 'Saving data success!', type: 'success', isLoading: false, autoClose: 3000 });
    };

    const onError = (error) => {
      toast.update(toastId, { render: getErrorMessage(error), type: 'error', isLoading: false, autoClose: 3000 });
    };

    if (selectedTab === CUBE_DATA_TABS.CUBE_ENTRY) {
      saveCubeDataEntry(payload, { onSuccess, onError });
    } else if (selectedTab === CUBE_DATA_TABS.BASE_VALUE) {
      saveCubeDataEntryBaseValue(payload, { onSuccess, onError });
    } else if (selectedTab === CUBE_DATA_TABS.INITIAL_VALUE) {
      saveCubeDataEntryInitialValue(payload, { onSuccess, onError });
    }
  };

  const handleTriggerSyncCubeData = () => {
    syncCubeData({
      cubeDefinitionId: cubeId,
      layerId: optionValue.layerId,
      memberId: optionValue.memberId,
      xMemberId: optionValue.xMemberId,
    });
  };

  const handleCloseModalView = () => {
    setCubeDimensionalViewModalState({
      open: false,
      cubeId: undefined,
    });
  };

  const handleShowCubeView = () => {
    navigate(
      {
        search: qs.stringify({
          layerId: optionValue.layerId,
          memberId: optionValue.memberId,
          xMemberId: optionValue.xMemberId,
          ...existingSearchParams,
        }),
      },
      {
        replace: true,
      },
    );

    cubeId &&
      setCubeDimensionalViewModalState({
        open: true,
        cubeId: cubeId,
      });
  };

  const isLoadingData = useMemo(() => {
    return isLoadingDataEntry || isLoadingDataInitialValue || isLoadingDataBaseValue;
  }, [isLoadingDataEntry, isLoadingDataInitialValue, isLoadingDataBaseValue]);

  const isSubmittingData = useMemo(() => {
    return isSubmitting || isSubmittingBaseValue || isSubmittingInitialValue;
  }, [isSubmitting, isSubmittingBaseValue, isSubmittingInitialValue]);

  const isCubeInCalculating =
    cubeActivityStatus === CUBE_ACTIVITY_STATUS.CALCULATING ||
    cubeActivityStatus === CUBE_ACTIVITY_STATUS.RECALCULATING;
  const isCubeFirstCalculating = cubeActivityStatus === CUBE_ACTIVITY_STATUS.CALCULATING;
  const isCubeRecalculating = cubeActivityStatus === CUBE_ACTIVITY_STATUS.RECALCULATING;

  const checkIsCellEditable = useCallback(
    (params) => {
      // the rowId is combination of attributename_datetimeperiod_uuid
      // ex: "tonnes_or_unit#19-02-23#6ecbc515-6773-454a-a40c-08f2a3166dfd"
      // return true;
      if (!isDataEditable || isCubeInCalculating) return false;

      const cellAttribute = params?.id?.split('#')?.[0] || '';
      const isInitialInputValue = inputInitialAttributes?.includes(cellAttribute) && firsTimePeriod === params.field;
      return !readOnlyAttributes?.includes(cellAttribute) && !isInitialInputValue && params.field !== 'Total';
    },
    [inputInitialAttributes, readOnlyAttributes, firsTimePeriod, isDataEditable, isCubeInCalculating],
  );

  const checkGetCellClassName = useCallback(
    (params) => {
      const { field } = params;
      const cellAttribute = params?.id?.split('#')?.[0] || '';
      const isCellAttributeInReadOnly = readOnlyAttributes?.includes(cellAttribute);
      const isCellAttributeInCalculated = calculatedAttributes?.includes(cellAttribute);
      const isCellAttributeInInputInitial = inputInitialAttributes?.includes(cellAttribute);
      const isInitialInputValue = isCellAttributeInInputInitial && firsTimePeriod === field;

      if (field === '__tree_data_group__') {
        return isCellAttributeInCalculated ? 'font-bold' : '';
      }

      let className = params.indexRelativeToCurrentPage % 2 === 0 ? '' : 'cube-odd';

      if (isCellAttributeInReadOnly || isInitialInputValue || field === 'Total' || !isDataEditable) {
        className += ' non-editable';
      } else {
        className += ' editable';
      }

      if (isCellAttributeInCalculated) className += ' calculated';
      return className;
    },
    [readOnlyAttributes, calculatedAttributes, inputInitialAttributes, firsTimePeriod, isDataEditable],
  );

  const handleOnRefresh = () => {
    refetchCubeData();
  };

  // Refresh the grid once, right when a (re)calculation transitions from
  // in-progress to finished, so the freshly computed values are shown.
  const wasCubeCalculatingRef = useRef(false);
  useEffect(() => {
    if (wasCubeCalculatingRef.current && !isCubeInCalculating) {
      handleOnRefresh();
    }
    wasCubeCalculatingRef.current = isCubeInCalculating;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCubeInCalculating]);

  const handleOnSeeFailureDetails = () => {
    const errorMessage = `Data update failed at ${dayjs(
      cubeActivityStatusData?.data?.lastModifiedDate,
    ).format(ENV_CONFIG.config.DATE_TIME_FORMAT)}.
    With error message: ${cubeActivityStatusData?.data?.activityStatusDescription}`;

    setModalInfo({
      visible: true,
      title: 'Data update Failed',
      descriptionMessage: errorMessage,
    });
  };

  const handleOnSelectedInsightView = (option: AutoCompleteItem) => {
    const url = `${PATH_CONSTANT.TENANT.CUBE.INSIGHT.replace(':cubeId', cubeId).replace(':insightId', option.value)}?${
      qs.stringify({
        memberId: optionValue.memberId,
        xMemberId: optionValue.xMemberId,
      }) || ''
    }`;
    navigate(url);
  };

  useEffect(() => {
    if (selectedTab !== CUBE_DATA_TABS.CUBE_ENTRY) setSelectedTab(CUBE_DATA_TABS.CUBE_ENTRY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cubeId]);

  useEffect(() => {
    if (selectedTab !== CUBE_DATA_TABS.CUBE_ENTRY) {
      apiRef.current.scrollToIndexes({ colIndex: 0 });
    }
  }, [selectedTab, apiRef]);

  const stepAutoScroll = useCallback(() => {
    if (!draggingRef.current) {
      rafRef.current && cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }

    const root = rangeContainerRef.current;
    if (root) {
      // Performance optimization: Use direct DOM element selection
      const scroller = (root.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement) || root;
      const rect = scroller.getBoundingClientRect();
      const { x, y } = mousePosRef.current;

      // Performance optimization: Pre-calculate edge positions
      const leftEdge = rect.left + EDGE_PX;
      const rightEdge = rect.right - EDGE_PX;
      const topEdge = rect.top + EDGE_PX;
      const bottomEdge = rect.bottom - EDGE_PX;

      let dx = 0,
        dy = 0;
      if (x < leftEdge) dx = -speedFor(leftEdge - x);
      else if (x > rightEdge) dx = speedFor(x - rightEdge);

      if (y < topEdge) dy = -speedFor(topEdge - y);
      else if (y > bottomEdge) dy = speedFor(y - bottomEdge);

      if (dx || dy) {
        const pos = apiRef.current.getScrollPosition?.() || { left: 0, top: 0 };
        const newLeft = Math.max(0, pos.left + dx);
        const newTop = Math.max(0, pos.top + dy);

        // Performance optimization: Only call scroll if position changed
        if (newLeft !== pos.left || newTop !== pos.top) {
          apiRef.current.scroll?.({ left: newLeft, top: newTop });
        }
      }
    }

    rafRef.current = requestAnimationFrame(stepAutoScroll);
  }, [apiRef]);

  const startAutoScroll = useCallback(() => {
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(stepAutoScroll);
  }, [stepAutoScroll]);
  function stopAutoScroll() {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }

  // Performance optimization: Memoized hasRange calculation
  const hasRange = useMemo(() => {
    return anchor && focus && (anchor.id !== focus.id || anchor.field !== focus.field);
  }, [anchor, focus]);

  // Map visible columns and rows to indices
  const colIndexByField = useMemo(() => {
    const m = new Map<string, number>();
    columns.forEach((c, i) => m.set(c.field, i));
    return m;
  }, [columns]);

  const rowIndexById = useMemo(() => {
    const m = new Map<GridRowId, number>();
    rowData.forEach((r, i) => m.set(r.rowid ?? r.id, i));
    return m;
  }, [rowData]);

  // Helper function to extract data from a selected range
  const extractRangeData = useCallback(() => {
    if (!anchor || !focus) return null;

    const R = normalizeRange(anchor, focus, rowIndexById, colIndexByField);
    if (!R) return null;

    // Performance optimization: Pre-allocate arrays instead of using push()
    const rowCount = R.rMax - R.rMin + 1;
    const colCount = R.cMax - R.cMin + 1;
    const rangeData: any[][] = new Array(rowCount);

    for (let ri = 0; ri < rowCount; ri++) {
      const currentRowData = rowData[R.rMin + ri];
      const rowValues: any[] = new Array(colCount);

      for (let ci = 0; ci < colCount; ci++) {
        const field = columns[R.cMin + ci].field;
        rowValues[ci] = currentRowData[field] ?? '';
      }

      rangeData[ri] = rowValues;
    }

    return rangeData;
  }, [anchor, focus, rowData, columns, rowIndexById, colIndexByField]);

  // Helper function to ensure values are not empty (converts to 0)
  const processEmptyValue = useCallback((value) => {
    if (value === null || value === undefined || value === '' || String(value).trim() === '') {
      return 0;
    }
    return value;
  }, []);

  const pushToUndoStack = useCallback((snapshot: any[]) => {
    undoStackRef.current.push(snapshot);
    if (undoStackRef.current.length > MAX_UNDO) {
      undoStackRef.current.shift();
    }
    redoStackRef.current = [];
  }, []);

  const handleEditCellChangeCommitted = useCallback(
    (params) => {
      const { id, field, value } = params;

      // Check if value is empty/null/undefined and set default to 0
      const processedValue = processEmptyValue(value);

      // Check if value is a command in format: <Integer>#<Value>
      const commandMatch = String(processedValue).match(/^(\d+)#(.*)$/);

      if (commandMatch) {
        const [, countStr, repeatValue] = commandMatch;
        const count = parseInt(countStr, 10);

        // Validation
        if (isNaN(count) || count <= 0) {
          toast.error(`Invalid command format: Count must be a positive number. Got: "${countStr}"`);
          return;
        }

        if (count > 100) {
          toast.error(`Command count too large: Maximum 100 columns allowed. Got: ${count}`);
          return;
        }

        // Find current column index
        const currentColIndex = columns.findIndex((col) => col.field === field);

        if (currentColIndex === -1) {
          toast.error(`Command failed: Could not find current column "${field}"`);
          return;
        }

        // Calculate how many columns we can actually fill
        const availableColumns = columns.length - currentColIndex;
        const actualCount = Math.min(count, availableColumns);
        let editableCount = 0;
        let skippedCount = 0;

        pushToUndoStack(rowData);
        setRowData(
          produce(rowData, (draft) => {
            const row = draft.find((row) => row.rowid === id);
            if (row) {
              // Repeat value across specified number of columns
              for (let i = 0; i < actualCount; i++) {
                const targetField = columns[currentColIndex + i].field;

                // Check if the target cell is editable before setting value
                if (checkIsCellEditable({ id, field: targetField })) {
                  // Set empty repeat values to 0
                  let finalValue = repeatValue;
                  if (
                    finalValue === null ||
                    finalValue === undefined ||
                    finalValue === '' ||
                    String(finalValue).trim() === ''
                  ) {
                    finalValue = 0;
                  }
                  row[targetField] = finalValue;
                  editableCount++;
                } else {
                  skippedCount++;
                }
              }
            }
          }),
        );

        // Show success/warning messages
        if (editableCount > 0) {
          const message =
            skippedCount > 0
              ? `Repeated "${repeatValue}" across ${editableCount} columns (${skippedCount} read-only columns skipped)`
              : `Repeated "${repeatValue}" across ${editableCount} columns`;
          toast.success(message);
        }

        if (actualCount < count) {
          toast.warning(`Only ${actualCount} columns available (requested ${count})`);
        }

        return; // Exit early to avoid normal value setting
      }

      // Normal single cell value update (fallback)
      pushToUndoStack(rowData);
      setRowData(
        produce(rowData, (draft) => {
          const row = draft.find((row) => row.rowid === id);
          if (row) {
            row[field] = processedValue;
          }
        }),
      );
    },
    [rowData, columns, checkIsCellEditable, processEmptyValue, pushToUndoStack],
  );

  const handleRepeatValuesApply = (count: number, value: string) => {
    handleEditCellChangeCommitted({
      id: repeatValuesModal.id,
      field: repeatValuesModal.field,
      value: `${count}#${value}`,
    });
    setRepeatValuesModal({ open: false, id: null, field: '' });
  };

  const handleCellInputApply = (value: string) => {
    handleEditCellChangeCommitted({
      id: cellInputModal.id,
      field: cellInputModal.field,
      value,
    });
    setCellInputModal({ open: false, id: null, field: '' });
  };

  const handleConfirmTabChange = () => {
    if (tabChangeModal.pendingTab) {
      setSelectedTab(tabChangeModal.pendingTab);
    }
    setTabChangeModal({ open: false, pendingTab: null });
  };

  const isInRange = useCallback(
    (id: GridRowId, field: string) => {
      if (!hasRange || !anchor || !focus) return false;
      const R = normalizeRange(anchor, focus, rowIndexById, colIndexByField);
      if (!R) return false;
      const ri = rowIndexById.get(id);
      const ci = colIndexByField.get(field);
      return ri! >= R.rMin && ri! <= R.rMax && ci! >= R.cMin && ci! <= R.cMax;
    },
    [hasRange, anchor, focus, rowIndexById, colIndexByField],
  );

  // Click/drag to define range
  const handleCellClick = useCallback(
    (params: any, event: React.MouseEvent) => {
      if ((event as any).button !== 0) return; // left click  only

      const newCell = { id: params.id, field: params.field };

      if (event.shiftKey && anchor) {
        // Performance optimization: Only update focus if it changed
        if (!focus || focus.id !== newCell.id || focus.field !== newCell.field) {
          setFocus(newCell);
        }
      } else {
        // Performance optimization: Batch anchor and focus updates
        setAnchor(newCell);
        setFocus(newCell);
      }
    },
    [anchor, focus],
  );
  const getEdgeFlags = useCallback(
    (id: GridRowId, field: string) => {
      if (!anchor || !focus) return null;
      const R = normalizeRange(anchor, focus, rowIndexById, colIndexByField);
      if (!R) return null;

      const ri = rowIndexById.get(id)!;
      const ci = colIndexByField.get(field)!;

      // Performance optimization: Use direct comparisons for range and edge detection
      const inRange = ri >= R.rMin && ri <= R.rMax && ci >= R.cMin && ci <= R.cMax;
      if (!inRange) return null;

      return {
        top: ri === R.rMin,
        bottom: ri === R.rMax,
        left: ci === R.cMin,
        right: ci === R.cMax,
      };
    },
    [anchor, focus, rowIndexById, colIndexByField],
  );

  const handleMouseDownCapture = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;

      // Performance optimization: Use direct DOM element selection
      const cell = (e.target as HTMLElement).closest('.MuiDataGrid-cell') as HTMLElement | null;
      if (!cell) return;

      // Stop native text selection
      e.preventDefault();

      const rowEl = cell.closest('.MuiDataGrid-row') as HTMLElement | null;
      if (!rowEl) return;

      const id = rowEl.getAttribute('data-id')!;
      const field = cell.getAttribute('data-field')!;

      // Performance optimization: Batch API calls
      try {
        apiRef.current.stopCellEditMode?.({ id, field, ignoreModifications: true });
        apiRef.current.setCellMode?.(id, field, 'view');
        (document.activeElement as HTMLElement | null)?.blur?.();
      } catch {}

      // ✅ START DRAG RANGE HERE
      setAnchor({ id, field });
      setFocus({ id, field });

      draggingRef.current = true;
      setDragging(true);

      mousePosRef.current = { x: e.clientX, y: e.clientY };
      startAutoScroll();
    },
    [apiRef, startAutoScroll],
  );

  const handleMouseUp = useCallback(() => {
    draggingRef.current = false;
    setDragging(false);
    stopAutoScroll();
  }, []);

  const clearRange = useCallback(() => {
    draggingRef.current = false;
    setDragging(false);
    setAnchor(null);
    setFocus(null);

    // Performance optimization: Clear caches when range is cleared
    rangeCache.clear();
    domCache.clear();
  }, []);

  const moveBy = useCallback(
    (fromId: any, fromField: string, dr: number, dc: number, extendSelection = false) => {
      const ri = rowIndexById.get(fromId);
      const ci = colIndexByField.get(fromField);
      if (ri == null || ci == null) return null;

      const newRi = Math.max(0, Math.min(rowData.length - 1, ri + dr));
      const newCi = Math.max(0, Math.min(columns.length - 1, ci + dc));

      const newRow = rowData[newRi];
      if (!newRow) return null;
      const newId = newRow.rowid ?? newRow.id;
      const newField = columns[newCi]?.field;
      if (newId == null || !newField) return null;

      if (extendSelection) {
        setFocus({ id: newId, field: newField });
      } else {
        setAnchor({ id: newId, field: newField });
        setFocus({ id: newId, field: newField });
      }

      try {
        apiRef.current.scrollToIndexes?.({ rowIndex: newRi, colIndex: newCi });
      } catch {}

      // Return the destination so callers can sync MUI's focus after the event loop
      return { newId, newField };
    },
    [rowData, columns, rowIndexById, colIndexByField, apiRef],
  );

  const handleCellKeyDown = useCallback(
    async (params: any, event: any) => {
      const isMeta = event.ctrlKey || event.metaKey;
      const key = event.key?.toLowerCase();

      // ---- COMMAND DETECTION ----
      // Handle special command key combination: Ctrl+Shift+R for repeat command
      if (isMeta && event.shiftKey && key === 'r') {
        event.preventDefault();
        setRepeatValuesModal({ open: true, id: params.id, field: params.field });
        return;
      }

      // Handle F2 key for value/command input
      if (key === 'f2') {
        event.preventDefault();
        setCellInputModal({ open: true, id: params.id, field: params.field });
        return;
      }

      // ---- UNDO ----
      if (isMeta && !event.shiftKey && key === 'z') {
        event.preventDefault();
        if (undoStackRef.current.length === 0) {
          toast.info('Nothing to undo');
          return;
        }
        const prev = undoStackRef.current.pop()!;
        redoStackRef.current.push(rowData);
        setRowData(prev);
        clearRange();
        return;
      }

      // ---- REDO ----
      if (isMeta && (key === 'y' || (event.shiftKey && key === 'z'))) {
        event.preventDefault();
        if (redoStackRef.current.length === 0) {
          toast.info('Nothing to redo');
          return;
        }
        const next = redoStackRef.current.pop()!;
        undoStackRef.current.push(rowData);
        setRowData(next);
        clearRange();
        return;
      }

      // ---- FILL DOWN (Ctrl+D) ----
      if (isMeta && !event.shiftKey && key === 'd') {
        event.preventDefault();
        if (!anchor || !focus || (anchor.id === focus.id && anchor.field === focus.field)) return;
        const R = normalizeRange(anchor, focus, rowIndexById, colIndexByField);
        if (!R || R.rMin === R.rMax) return;
        pushToUndoStack(rowData);
        setRowData((prev) => {
          const next = [...prev];
          for (let ci = R.cMin; ci <= R.cMax; ci++) {
            const field = columns[ci].field;
            const topValue = prev[R.rMin][field];
            for (let ri = R.rMin + 1; ri <= R.rMax; ri++) {
              const row = { ...next[ri] };
              if (checkIsCellEditable({ id: row.rowid ?? row.id, field })) {
                row[field] = topValue;
              }
              next[ri] = row;
            }
          }
          return next;
        });
        toast.success('Filled down');
        return;
      }

      // ---- FILL RIGHT (Ctrl+R, no shift) ----
      if (isMeta && !event.shiftKey && key === 'r') {
        event.preventDefault();
        if (!anchor || !focus || (anchor.id === focus.id && anchor.field === focus.field)) return;
        const R = normalizeRange(anchor, focus, rowIndexById, colIndexByField);
        if (!R || R.cMin === R.cMax) return;
        pushToUndoStack(rowData);
        setRowData((prev) => {
          const next = [...prev];
          for (let ri = R.rMin; ri <= R.rMax; ri++) {
            const leftField = columns[R.cMin].field;
            const leftValue = prev[ri][leftField];
            const row = { ...next[ri] };
            for (let ci = R.cMin + 1; ci <= R.cMax; ci++) {
              const field = columns[ci].field;
              if (checkIsCellEditable({ id: row.rowid ?? row.id, field })) {
                row[field] = leftValue;
              }
            }
            next[ri] = row;
          }
          return next;
        });
        toast.success('Filled right');
        return;
      }

      // ---- CLEAR (Delete key, view mode only) ----
      if (key === 'delete') {
        try {
          if (apiRef.current.getCellMode?.(params.id, params.field) === 'edit') return;
        } catch {}
        event.preventDefault();
        const isSingleSel = !anchor || !focus || (anchor.id === focus.id && anchor.field === focus.field);
        if (isSingleSel) {
          const cellId = anchor?.id ?? params.id;
          const cellField = anchor?.field ?? params.field;
          if (checkIsCellEditable({ id: cellId, field: cellField })) {
            pushToUndoStack(rowData);
            setRowData((prev) =>
              prev.map((row) => ((row.rowid ?? row.id) === cellId ? { ...row, [cellField]: 0 } : row)),
            );
          }
        } else {
          const R = normalizeRange(anchor!, focus!, rowIndexById, colIndexByField);
          if (!R) return;
          pushToUndoStack(rowData);
          setRowData((prev) => {
            const next = [...prev];
            for (let ri = R.rMin; ri <= R.rMax; ri++) {
              const row = { ...next[ri] };
              for (let ci = R.cMin; ci <= R.cMax; ci++) {
                const field = columns[ci].field;
                if (checkIsCellEditable({ id: row.rowid ?? row.id, field })) {
                  row[field] = 0;
                }
              }
              next[ri] = row;
            }
            return next;
          });
          toast.success(`Cleared ${(R.rMax - R.rMin + 1) * (R.cMax - R.cMin + 1)} cells`);
        }
        return;
      }

      // ---- COPY (single cell or range) ----
      if (isMeta && key === 'c') {
        // single when no range OR anchor===focus
        const single = !anchor || !focus || (anchor.id === focus.id && anchor.field === focus.field);

        if (single) {
          event.preventDefault();

          // prefer live edit value if the cell is in edit mode
          let val = params.value;
          try {
            const mode = apiRef.current.getCellMode?.(params.id, params.field);
            if (mode === 'edit') {
              const p = apiRef.current.getEditCellValueParams?.(params.id, params.field);
              if (p) val = p.value;
            }
          } catch {}

          const text = String(val ?? '');
          copiedRef.current = { value: val };

          try {
            await copyToClipboard(text);
            toast.success('Value copied to clipboard');
          } catch (err) {
            toast.error('Copy failed');
            console.error('Copy failed', err);
          }
        } else {
          // Range copy
          event.preventDefault();
          const rangeData = extractRangeData();
          if (!rangeData) return;

          // Convert to tab-separated values
          const text = rangeData.map((row) => row.join('\t')).join('\n');
          copiedRef.current = { value: rangeData, isRange: true };

          try {
            await copyToClipboard(text);
            toast.success(`Copied ${rangeData.length}×${rangeData[0]?.length || 0} range to clipboard`);
          } catch (err) {
            toast.error('Copy failed');
            console.error('Copy failed', err);
          }
        }
        return;
      }

      // ---- PASTE (single cell or range) ----
      if (isMeta && key === 'v') {
        if (!copiedRef.current || !anchor || !focus) return;
        event.preventDefault();

        if (copiedRef.current.isRange) {
          // Excel-like paste: Auto-expand from anchor cell to match copied data dimensions
          const rangeData = copiedRef.current.value;
          const rangeRows = rangeData.length;
          const rangeCols = rangeData[0]?.length || 0;

          // Find anchor position
          const anchorRowIndex = rowIndexById.get(anchor.id);
          const anchorColIndex = colIndexByField.get(anchor.field);

          if (anchorRowIndex == null || anchorColIndex == null) return;

          // Calculate target range based on copied data size
          const targetRowStart = anchorRowIndex;
          const targetRowEnd = Math.min(anchorRowIndex + rangeRows - 1, rowData.length - 1);
          const targetColStart = anchorColIndex;
          const targetColEnd = Math.min(anchorColIndex + rangeCols - 1, columns.length - 1);

          const actualRows = targetRowEnd - targetRowStart + 1;
          const actualCols = targetColEnd - targetColStart + 1;

          // Show feedback about the operation
          if (actualRows < rangeRows || actualCols < rangeCols) {
            toast.warning(`Pasted ${actualRows}×${actualCols} cells (copied data was ${rangeRows}×${rangeCols})`);
          } else {
            toast.success(`Pasted ${actualRows}×${actualCols} cells`);
          }

          pushToUndoStack(rowData);
          setRowData((prev) => {
            const next = [...prev];
            let skippedCount = 0;

            for (let ri = 0; ri < actualRows && targetRowStart + ri < next.length; ri++) {
              const targetRowIndex = targetRowStart + ri;
              const row = { ...next[targetRowIndex] };

              for (let ci = 0; ci < actualCols && targetColStart + ci < columns.length; ci++) {
                const targetColIndex = targetColStart + ci;
                const field = columns[targetColIndex].field;

                if (!checkIsCellEditable({ id: row.rowid ?? row.id, field })) {
                  skippedCount++;
                  continue;
                }

                // Get data from copied range (with bounds checking)
                const sourceValue = ri < rangeRows && ci < rangeCols ? rangeData[ri][ci] : 0;
                row[field] = processEmptyValue(sourceValue);
              }
              next[targetRowIndex] = row;
            }

            // Show additional feedback about skipped cells
            if (skippedCount > 0) {
              toast.info(`${skippedCount} read-only cells were skipped`);
            }

            return next;
          });

          // Clear the selection and set focus to the end of pasted range
          setAnchor({
            id: rowData[targetRowEnd]?.rowid ?? rowData[targetRowEnd]?.id,
            field: columns[targetColEnd].field,
          });
          setFocus({
            id: rowData[targetRowEnd]?.rowid ?? rowData[targetRowEnd]?.id,
            field: columns[targetColEnd].field,
          });
        } else {
          // Paste single cell value - Excel-like behavior
          const copiedValue = copiedRef.current!.value;
          const commandMatch = String(copiedValue).match(/^(\d+)#(.*)$/);

          if (commandMatch) {
            // Handle command paste - only apply to the anchor cell
            handleEditCellChangeCommitted({
              id: anchor.id,
              field: anchor.field,
              value: copiedValue,
            });
          } else {
            // If there's a range selection, paste to the entire range
            // If there's only a single cell selected (anchor === focus), just paste to that cell
            const isSingleCellSelection = anchor.id === focus.id && anchor.field === focus.field;

            if (isSingleCellSelection) {
              // Single cell paste
              if (checkIsCellEditable({ id: anchor.id, field: anchor.field })) {
                pushToUndoStack(rowData);
                setRowData((prev) =>
                  prev.map((row) => {
                    if ((row.rowid ?? row.id) === anchor.id) {
                      return { ...row, [anchor.field]: processEmptyValue(copiedValue) };
                    }
                    return row;
                  }),
                );
                toast.success('Value pasted');
              } else {
                toast.error('Cannot paste to read-only cell');
              }
            } else {
              // Range paste - fill the selected range with the single value
              const R = normalizeRange(anchor, focus, rowIndexById, colIndexByField);
              if (!R) return;

              let pastedCount = 0;
              let skippedCount = 0;

              pushToUndoStack(rowData);
              setRowData((prev) => {
                const next = [...prev];
                for (let ri = R.rMin; ri <= R.rMax; ri++) {
                  const row = { ...next[ri] };
                  for (let ci = R.cMin; ci <= R.cMax; ci++) {
                    const field = columns[ci].field;
                    if (!checkIsCellEditable({ id: row.rowid ?? row.id, field })) {
                      skippedCount++;
                      continue;
                    }

                    row[field] = processEmptyValue(copiedValue);
                    pastedCount++;
                  }
                  next[ri] = row;
                }
                return next;
              });

              toast.success(
                `Pasted to ${pastedCount} cells${skippedCount > 0 ? ` (${skippedCount} read-only cells skipped)` : ''}`,
              );
            }
          }
        }
        clearRange();
      }

      // ---- ARROW KEY NAVIGATION ----
      if (key === 'arrowup' || key === 'arrowdown' || key === 'arrowleft' || key === 'arrowright') {
        try {
          // Excel behaviour: arrow key in edit mode commits the value then navigates
          if (apiRef.current.getCellMode?.(params.id, params.field) === 'edit') {
            apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
          }
        } catch {}
        event.preventDefault();
        const dr = key === 'arrowdown' ? 1 : key === 'arrowup' ? -1 : 0;
        const dc = key === 'arrowright' ? 1 : key === 'arrowleft' ? -1 : 0;
        let moved: { newId: any; newField: string } | null | undefined;
        if (event.shiftKey) {
          // Extend selection: keep anchor, move focus
          const currentAnchor = anchor ?? { id: params.id, field: params.field };
          if (!anchor) setAnchor(currentAnchor);
          const fromId = focus?.id ?? currentAnchor.id;
          const fromField = focus?.field ?? currentAnchor.field;
          moved = moveBy(fromId, fromField, dr, dc, true);
        } else {
          // Move selection: collapse to anchor and navigate
          const fromId = anchor?.id ?? params.id;
          const fromField = anchor?.field ?? params.field;
          moved = moveBy(fromId, fromField, dr, dc, false);
        }
        // Sync MUI's internal focus AFTER its own navigation handler has run
        if (moved) {
          const { newId, newField } = moved;
          requestAnimationFrame(() => {
            try {
              apiRef.current.setCellFocus?.(newId, newField);
            } catch {}
          });
        }
        return;
      }

      // ---- ENTER: commit edit + move down / up ----
      if (key === 'enter' && !isMeta) {
        try {
          const mode = apiRef.current.getCellMode?.(params.id, params.field);
          if (mode === 'edit') {
            event.preventDefault();
            apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
            const fromId = anchor?.id ?? params.id;
            const fromField = anchor?.field ?? params.field;
            const shiftKey = event.shiftKey;
            requestAnimationFrame(() => {
              const moved = moveBy(fromId, fromField, shiftKey ? -1 : 1, 0, false);
              if (moved) {
                try {
                  apiRef.current.setCellFocus?.(moved.newId, moved.newField);
                } catch {}
              }
            });
            return;
          }
        } catch {}
      }

      // ---- TAB: commit edit + move to next / prev editable cell in row ----
      if (key === 'tab' && !isMeta) {
        event.preventDefault();
        try {
          const mode = apiRef.current.getCellMode?.(params.id, params.field);
          if (mode === 'edit') {
            apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
          }
        } catch {}
        const dc = event.shiftKey ? -1 : 1;
        const fromId = anchor?.id ?? params.id;
        const fromField = anchor?.field ?? params.field;
        const ri = rowIndexById.get(fromId);
        const ci = colIndexByField.get(fromField);
        if (ri != null && ci != null) {
          let newCi = ci + dc;
          while (newCi >= 0 && newCi < columns.length) {
            const candidateField = columns[newCi].field;
            if (checkIsCellEditable({ id: fromId, field: candidateField })) {
              setAnchor({ id: fromId, field: candidateField });
              setFocus({ id: fromId, field: candidateField });
              try {
                apiRef.current.scrollToIndexes?.({ rowIndex: ri, colIndex: newCi });
              } catch {}
              const tabTargetId = fromId;
              const tabTargetField = candidateField;
              requestAnimationFrame(() => {
                try {
                  apiRef.current.setCellFocus?.(tabTargetId, tabTargetField);
                } catch {}
              });
              break;
            }
            newCi += dc;
          }
        }
        return;
      }

      // ---- TYPE TO START EDITING (digit keys replace current value) ----
      if (key.length === 1 && /\d/.test(key) && !isMeta && !event.altKey && !event.shiftKey) {
        try {
          const mode = apiRef.current.getCellMode?.(params.id, params.field);
          if (mode !== 'edit' && checkIsCellEditable({ id: params.id, field: params.field })) {
            event.preventDefault();
            apiRef.current.startCellEditMode?.({ id: params.id, field: params.field });
            const digitValue = parseInt(key, 10);
            requestAnimationFrame(() => {
              try {
                apiRef.current.setEditCellValue?.({ id: params.id, field: params.field, value: digitValue });
              } catch {}
            });
            return;
          }
        } catch {}
      }
    },
    [
      anchor,
      focus,
      columns,
      checkIsCellEditable,
      apiRef,
      handleEditCellChangeCommitted,
      rowData,
      extractRangeData,
      clearRange,
      rowIndexById,
      colIndexByField,
      processEmptyValue,
      pushToUndoStack,
      moveBy,
    ],
  );

  const getCellClassNameCombined = useCallback(
    (params: any) => {
      const base = checkGetCellClassName(params) || '';
      const flags = getEdgeFlags(params.id, params.field);
      const inRange = !!flags;

      // Performance optimization: Use conditional string building instead of template literals
      let className = base;
      if (inRange) className += ' rangeCell';
      if (flags?.top) className += ' rangeTop';
      if (flags?.bottom) className += ' rangeBottom';
      if (flags?.left) className += ' rangeLeft';
      if (flags?.right) className += ' rangeRight';

      return className;
    },
    [checkGetCellClassName, getEdgeFlags],
  );

  const handleOnRightClickMenuAction = useCallback(
    async (action: CubeEntryRightClickMenuAction) => {
      console.log('action', action);
      if (action === CubeEntryRightClickMenuAction.Copy) {
        // Check if we have a range selection
        const hasRange = anchor && focus && (anchor.id !== focus.id || anchor.field !== focus.field);

        if (hasRange) {
          // Copy range
          const rangeData = extractRangeData();
          if (!rangeData) return;

          const text = rangeData.map((row) => row.join('\t')).join('\n');
          copiedRef.current = { value: rangeData, isRange: true };

          try {
            await copyToClipboard(text);
            toast.success(`Copied ${rangeData.length}×${rangeData[0]?.length || 0} range to clipboard`);
          } catch (err) {
            toast.error('Copy failed');
            console.error('Copy failed', err);
          }
        } else {
          // Copy single cell
          const val = apiRef.current.getCellValue?.(anchor!.id, anchor!.field);
          copiedRef.current = { value: val };

          try {
            await copyToClipboard(String(val ?? ''));
            toast.success('Value copied to clipboard');
          } catch (err) {
            toast.error('Copy failed');
            console.error('Copy failed', err);
          }
        }
        setMenuPos(null);
      } else if (action === CubeEntryRightClickMenuAction.Paste) {
        const fake = { ctrlKey: true, metaKey: true, key: 'v', preventDefault() {} };
        handleCellKeyDown({ id: anchor?.id, field: anchor?.field }, fake);
        setMenuPos(null);
      } else if (action === CubeEntryRightClickMenuAction.RepeatValues) {
        const fake = { ctrlKey: true, metaKey: true, shiftKey: true, key: 'r', preventDefault() {} };
        handleCellKeyDown({ id: anchor?.id, field: anchor?.field }, fake);
        setMenuPos(null);
      } else if (action === CubeEntryRightClickMenuAction.FillDown) {
        const fake = { ctrlKey: true, metaKey: true, shiftKey: false, key: 'd', preventDefault() {} };
        handleCellKeyDown({ id: anchor?.id, field: anchor?.field }, fake);
        setMenuPos(null);
      } else if (action === CubeEntryRightClickMenuAction.FillRight) {
        const fake = { ctrlKey: true, metaKey: true, shiftKey: false, key: 'r', preventDefault() {} };
        handleCellKeyDown({ id: anchor?.id, field: anchor?.field }, fake);
        setMenuPos(null);
      } else if (action === CubeEntryRightClickMenuAction.Clear) {
        const fake = { ctrlKey: false, metaKey: false, shiftKey: false, key: 'delete', preventDefault() {} };
        handleCellKeyDown({ id: anchor?.id, field: anchor?.field }, fake);
        setMenuPos(null);
      } else if (action === CubeEntryRightClickMenuAction.Close) {
        setMenuPos(null);
      }
    },
    [anchor, focus, handleCellKeyDown, extractRangeData, apiRef],
  );

  useEffect(() => {
    const root = rangeContainerRef.current;
    if (!root) return;

    function handleContextMenu(e: MouseEvent) {
      const cell = (e.target as HTMLElement).closest('.MuiDataGrid-cell') as HTMLElement | null;
      if (!cell) return; // clicks in headers / empty space -> let browser menu show

      e.preventDefault(); // block default browser menu

      const row = cell.closest('.MuiDataGrid-row') as HTMLElement;
      const id = row.getAttribute('data-id')!;
      const field = cell.getAttribute('data-field')!;

      // if click is outside current rectangle => collapse to single-cell selection
      if (!isInRange(id, field)) {
        setAnchor({ id, field });
        setFocus({ id, field });
      }

      setMenuPos({ x: e.clientX + 2, y: e.clientY + 2 });
    }

    root.addEventListener('contextmenu', handleContextMenu);
    return () => root.removeEventListener('contextmenu', handleContextMenu);
  }, [isInRange]);

  // Performance optimization: Create throttled mouse move handler
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!draggingRef.current) return;
      mousePosRef.current = { x: e.clientX, y: e.clientY }; // <-- keep updated

      // Performance optimization: Use direct DOM element selection
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      if (!el) return;

      const cell = el.closest('.MuiDataGrid-cell') as HTMLElement | null;
      if (!cell) return;

      const rowEl = cell.closest('.MuiDataGrid-row') as HTMLElement | null;
      if (!rowEl) return;

      const id = rowEl.getAttribute('data-id');
      const field = cell.getAttribute('data-field');

      if (!id || !field) return;

      // Performance optimization: Only update focus if position changed
      const newFocus = { id, field };
      if (!focus || focus.id !== newFocus.id || focus.field !== newFocus.field) {
        setFocus(newFocus);
      }
    },
    [focus],
  );

  const throttledMouseMove = useMemo(() => _.throttle(handleMouseMove, 16), [handleMouseMove]);

  // Attach native listeners on the wrapper around the grid
  useEffect(() => {
    document.addEventListener('mousemove', throttledMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', throttledMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      throttledMouseMove.cancel(); // Cancel any pending throttled calls
    };
  }, [throttledMouseMove, handleMouseUp]);

  // Reset range when tab changes or cubeId changes
  useEffect(() => {
    setAnchor(null);
    setFocus(null);

    // Performance optimization: Clear caches when data changes
    rangeCache.clear();
    domCache.clear();
  }, [cubeId, selectedTab]);

  return (
    <>
      <TenantHomeDashboardNavigationBar
        error={error}
        breadCrumbs={breadCrumbs}
        containerSx={{ height: '100%', flex: 1, flexDirection: 'column', overflowY: 'auto', display: 'flex' }}
        rootContainerSx={{ height: '95vh' }}
      >
        {isCubeRecalculating && (
          <Alert
            severity='info'
            sx={{ mb: 2, alignItems: 'center' }}
            icon={<LinearProgress sx={{ width: 80, mt: 1 }} />}
          >
            Data is being updated. You can still view the data. Editing is paused until this finishes.
          </Alert>
        )}
        {isDataEditable && cubeActivityStatus === CUBE_ACTIVITY_STATUS.CALCULATION_FAILED && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            The latest data update didn&apos;t complete. You&apos;re seeing values from the last successful run.{' '}
            <span style={moreDetailsTextActionStyle} onClick={handleOnSeeFailureDetails}>
              <u>
                <b>See details</b>
              </u>
            </span>
          </Alert>
        )}
        <AnimatedPage
          key={cubeId || 'cube-id'}
          variants={getPageAnimationByRefNavigation(navigationRef)}
          style={{
            height: '100%',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
          }}
        >
          <CubeDataEntrySettingPanel
            ref={cubeSettingPanelRef}
            cubeId={cubeId}
            disabled={isLoadingData || isSubmittingData}
            loading={isLoadingData || isSubmittingData}
            setIsLoadingOptionsData={setIsLoadingOptionsData}
            onOptionChange={handleOnOptionChange}
            showSaveButton={isAnyUnsaved && !isCubeInCalculating}
            onButtonSaveClick={handleOnSaveData}
            onButtonSycnClick={handleTriggerSyncCubeData}
            onRefresh={handleOnRefresh}
            onShowDataDetails={handleOnShowDataDetails}
            onDisplayChart={handleOnDisplayChart}
            onShowCubeView={handleShowCubeView}
            onInsightMenuOptionClick={handleOnSelectedInsightView}
            showSyncButton={cubeEntryData?.data.cubeSync?.status === CUBE_SYNC_STATUS.REQUIRED}
            hideIntervalOption={selectedTab !== CUBE_DATA_TABS.CUBE_ENTRY}
          />

          {tabsMenu.length > 1 && (
            <Stack direction='row' flex spacing={5} alignItems='center' justifyContent='space-between' width='100%'>
              <Box px={3.5} sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                <Tabs
                  sx={{
                    width: '100%',
                  }}
                  TabIndicatorProps={TabIndicatoryStyle}
                  value={selectedTab}
                  onChange={handleOnTabChange}
                >
                  {tabsMenu.map((tab) => (
                    <Tab sx={TabStyle} value={tab.value} label={tab.label} key={tab.value} />
                  ))}
                </Tabs>
              </Box>
            </Stack>
          )}

          {isCubeFirstCalculating && (
            <Stack sx={{ height: 400, justifyContent: 'center', alignItems: 'center' }}>
              <Stack width='20%'>
                <Typography variant='input-label-gray' mb={2} textAlign='center'>
                  Setting up your cube for the first time…
                </Typography>
                <LinearProgress />
              </Stack>
            </Stack>
          )}

          <CubeEntryRightClickMenu
            onAction={handleOnRightClickMenuAction}
            open={!!menuPos}
            menuPos={menuPos}
            isSingleCell={isSingleCell}
            hasClipboard={hasClipboard}
          />

          {!isCubeFirstCalculating && !isLoadingData && !isLoadingOptionsData && (
            <CubeEntryLegend
              onOpenDeveloperTools={
                ENV_CONFIG.featureFlags.SHOW_DEV_TOOLS
                  ? () => setDeveloperToolsModal({ open: true })
                  : undefined
              }
              onOpenReferenceAudit={
                ENV_CONFIG.featureFlags.SHOW_DEV_TOOLS
                  ? () => setReferenceAuditModal({ open: true })
                  : undefined
              }
            />
          )}

          {isLoadingData || isLoadingOptionsData ? (
            <Stack direction='column' sx={{ bgcolor: '#fff', py: 3, height: '300px', px: 2 }} justifyContent='center'>
              <ActivityIndicator />
            </Stack>
          ) : (
            (cubeActivityStatus === CUBE_ACTIVITY_STATUS.READY ||
              cubeActivityStatus === CUBE_ACTIVITY_STATUS.CALCULATION_FAILED ||
              cubeActivityStatus === CUBE_ACTIVITY_STATUS.RECALCULATING) && (
              <Box
                ref={rangeContainerRef}
                onMouseDownCapture={handleMouseDownCapture}
                sx={{
                  height: '100%',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                <HoverPopperProvider>
                  <CustomDataTable
                    loading={isSubmittingData}
                    apiRef={apiRef}
                    isError={isErrorCubeData}
                    errorTitle={getErrorTitle(errorCubeData)}
                    errorMessage={getErrorMessage(errorCubeData)}
                    initialState={{
                      pinnedColumns: { left: [GRID_TREE_DATA_GROUPING_FIELD] },
                      pagination: {
                        page: 0,
                      },
                      detailPanel: {
                        expandedRowIds: [1],
                      },
                    }}
                    columns={columns}
                    rows={isErrorCubeData ? [] : rowData}
                    treeData={!isErrorCubeData}
                    getRowId={(row) => row?.rowid || 0}
                    getTreeDataPath={(row) => row.path}
                    groupingColDef={!isErrorCubeData && groupingColDef}
                    disableSelectionOnClick
                    onCellEditCommit={handleEditCellChangeCommitted}
                    processRowUpdate={(newRow, oldRow) => {
                      // Handle direct row updates and ensure empty values become 0
                      const updatedRow = { ...newRow };
                      Object.keys(updatedRow).forEach((key) => {
                        if (key !== 'rowid' && key !== 'id' && key !== 'path' && key !== 'name') {
                          updatedRow[key] = processEmptyValue(updatedRow[key]);
                        }
                      });

                      // Update the state to reflect the changes
                      setRowData((prevData) =>
                        prevData.map((row) =>
                          (row.rowid || row.id) === (updatedRow.rowid || updatedRow.id) ? updatedRow : row,
                        ),
                      );

                      return updatedRow;
                    }}
                    checkboxSelection={false}
                    rowsPerPageOptions={[5, 10, 20]}
                    isCellEditable={checkIsCellEditable}
                    isGroupExpandedByDefault={(params) => {
                      return true;
                    }}
                    onCellClick={handleCellClick}
                    onCellKeyDown={handleCellKeyDown}
                    getCellClassName={getCellClassNameCombined}
                    paginationMode='client'
                    editMode='cell'
                    sortingMode='client'
                    columnBuffer={6}
                    columnThreshold={6}
                    rowBuffer={15}
                    rowThreshold={15}
                    className={dragging ? 'range-dragging' : undefined}
                    sx={{
                      height: '100%',
                      minHeight: 'calc(100vh - 400px)',
                      flex: 1,
                      mt: 1,
                      '&.range-dragging .MuiDataGrid-cellContent': { userSelect: 'none' },

                      // fill
                      '& .rangeCell': (theme) => ({
                        backgroundColor: theme.palette.action.selected + ' !important',
                      }),

                      // draw only the outer rectangle
                      '& .rangeTop': (theme) => ({ borderTop: `1px solid ${theme.palette.primary.main} !important` }),
                      '& .rangeBottom': (theme) => ({
                        borderBottom: `1px solid ${theme.palette.primary.main} !important`,
                      }),
                      '& .rangeLeft': (theme) => ({ borderLeft: `1px solid ${theme.palette.primary.main} !important` }),
                      '& .rangeRight': (theme) => ({
                        borderRight: `1px solid ${theme.palette.primary.main} !important`,
                      }),
                    }}
                    filterMode='client'
                    autoHeight={false}
                    hideFooter
                    components={{
                      Toolbar: null,
                      Pagination: null,
                    }}
                  />
                </HoverPopperProvider>
              </Box>
            )
          )}
        </AnimatedPage>
        <ModalLeavePageConfirmationComponent when={isAnyUnsaved} />
      </TenantHomeDashboardNavigationBar>
      <ModalInfoComponent
        visible={modalInfo.visible}
        title={modalInfo.title}
        descriptionMessage={modalInfo.descriptionMessage}
        onClose={() => setModalInfo({ ...modalInfo, visible: false })}
      />
      <CubeDimensionalViewModal
        open={cubeDimensionalViewModalState.open}
        onClose={handleCloseModalView}
        cubeId={cubeId}
      />
      <ModalCubeEventDataDetails
        open={modalDataDetails.open}
        onClose={() => {
          setModalDataDetails(initialStateModalDetails);
        }}
        cubeId={cubeId}
        cubeMemberName={cubeEntryData?.data.member?.label}
        title={`${cubeEntryData?.data.member?.label} - Data Details`}
        subtitle={`${modalDataDetails.eventSubjectName} • ${modalDataDetails.timePeriod}`}
        timePeriod={modalDataDetails.timePeriod || undefined}
        cubeMember={modalDataDetails.cubeMember || undefined}
        timeInterval={modalDataDetails.timeInterval || undefined}
        subjectId={modalDataDetails.eventSubjectId || undefined}
      />
      <ModalRepeatValues
        open={repeatValuesModal.open}
        onClose={() => setRepeatValuesModal({ open: false, id: null, field: '' })}
        onApply={handleRepeatValuesApply}
      />
      <ModalCellInput
        open={cellInputModal.open}
        onClose={() => setCellInputModal({ open: false, id: null, field: '' })}
        onApply={handleCellInputApply}
      />
      <ModalConfirmationComponent
        visible={tabChangeModal.open}
        title='Unsaved Changes'
        message='You have unsaved changes. Switching tabs will discard them.'
        messageType='warning'
        labelButtonApprove='Discard & Switch'
        labelButtonCancel='Stay on this tab'
        onApprove={handleConfirmTabChange}
        onCancel={() => setTabChangeModal({ open: false, pendingTab: null })}
        onClose={() => setTabChangeModal({ open: false, pendingTab: null })}
      />
      <ModalCubeChartDisplay
        open={chartDisplayModal.open}
        onClose={() => setChartDisplayModal({ open: false })}
        cubeId={cubeId}
        initialMemberId={optionValue.memberId}
        initialXMemberId={optionValue.xMemberId}
        initialLayerIds={chartDisplayModal.layerIds}
        initialTimeIntervalId={optionValue.timeIntervalId}
        initialAttributes={chartDisplayModal.initialAttributes}
        initialChartType={chartDisplayModal.chartType}
      />
      {ENV_CONFIG.featureFlags.SHOW_DEV_TOOLS && (
        <ModalDeveloperToolsAudit
          open={developerToolsModal.open}
          onClose={() => setDeveloperToolsModal({ open: false })}
          cubeId={cubeId}
          layerId={optionValue.layerId}
          layerName={cubeEntryData?.data?.layer?.layerName}
        />
      )}
      {ENV_CONFIG.featureFlags.SHOW_DEV_TOOLS && (
        <ModalDeveloperToolsReferenceAudit
          open={referenceAuditModal.open}
          onClose={() => setReferenceAuditModal({ open: false })}
          cubeId={cubeId}
          memberId={optionValue.memberId}
          layerId={optionValue.layerId}
          xMemberId={optionValue.xMemberId}
          timeIntervalId={optionValue.timeIntervalId}
          layerName={cubeEntryData?.data?.layer?.layerName}
          memberName={cubeEntryData?.data?.member?.label}
        />
      )}
    </>
  );
}
